import { assign, createMachine, DoneActorEvent, fromPromise } from "xstate";
import {
  CheckoutMachineContext,
  CheckoutMachineEvents,
  CheckoutRetryCheckoutEvent,
  CheckoutState,
  CheckoutSubmitUserInteractionEvent,
  CheckoutUpdateRetailerCheckoutOrderEvent,
  UserRetailerCheckoutRetailerCredentialsCommunicationResponse,
} from "./types/CheckoutMachine";
import {
  getErrorTypeFromCheckoutOrder,
  getActiveStageFromCheckoutSection,
  getSectionStateFromCheckoutOrder,
} from "../utils";
import {
  UserInteractionType,
  UserRetailerCheckoutOrder,
  UserRetailerCheckoutOrderStatusV2,
} from "../types";
import {
  getRetailerCheckoutOrderStatusV2,
  getRetailerCheckoutOrderStatusV2Mock,
  reSubmitCheckout,
  RetailerCheckoutOrderStatusV2,
  submitUserInteraction,
  switchToGuest,
} from "@/api/rest/checkoutApi";
import { getTOtp } from "@/api/rest/authenticated";
import { encryptData } from "@/utils/encryptionUtils";

export type CheckoutMachineType = typeof checkoutMachine;
export const MOCKING_CHECKOUT = false;

// Poll interval in milliseconds
const POLL_INTERVAL = 3000;

const retryCheckoutActor = fromPromise(
  async ({
    input,
  }: {
    input: {
      context: CheckoutMachineContext;
      event: CheckoutMachineEvents;
    };
  }) => {
    const { orderId } = input.context;
    if (!orderId) {
      throw new Error("No order ID provided for polling");
    }
    const eventData = (input.event as CheckoutRetryCheckoutEvent).data;
    const { retailerId, credential } = eventData ?? {};
    if (credential && retailerId && orderId) {
      const { currentStamp, token } = (await getTOtp()) ?? {};
      const connectionInfo = encryptData(
        {
          retailerId,
          credential,
        },
        token
      );
      const res = await reSubmitCheckout({
        orderId,
        currentStamp,
        connectionInfo,
      });
      return res;
    }

    const payment = eventData?.payment;
    const res = await reSubmitCheckout({ orderId, payment });
    return res;
  }
);

const switchToGuestActor = fromPromise(
  async ({
    input,
  }: {
    input: {
      context: CheckoutMachineContext;
      event: CheckoutMachineEvents;
    };
  }) => {
    const { orderId } = input.context;
    if (!orderId) {
      throw new Error("No order ID provided for polling");
    }

    const res = await switchToGuest({ orderId });
    return res;
  }
);

const pollCheckoutStatusActor = fromPromise(
  async ({
    input: { context, event },
  }: {
    input: {
      context: CheckoutMachineContext;
      event: CheckoutMachineEvents;
    };
  }) => {
    try {
      const { orderId } = context;
      if (!orderId) {
        throw new Error("No order ID provided for polling");
      }

      const res = MOCKING_CHECKOUT
        ? await getRetailerCheckoutOrderStatusV2Mock({ orderId })
        : await getRetailerCheckoutOrderStatusV2({ orderId });

      return res;
    } catch (e) {
      throw e;
    }
  }
);

const submitUserInteractionApi = fromPromise(
  async ({
    input,
  }: {
    input: {
      context: CheckoutMachineContext;
      event: CheckoutMachineEvents;
    };
  }) => {
    const order =
      input.context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder;
    const communicationId = order?.userInteractionRequirement?.communicationId;
    const orderId = order?.orderId;

    const request = (input.event as CheckoutSubmitUserInteractionEvent).data
      .interactionData;

    if (!orderId || !communicationId) {
      throw new Error("No order ID or communication ID provided");
    }

    const actionType = order.userInteractionRequirement?.type;

    const hasOrderTimeout =
      order?.status ===
      UserRetailerCheckoutOrderStatusV2.user_interaction_timeout;

    const isTimeoutConnect =
      actionType === UserInteractionType.RETAILER_CREDENTIALS &&
      hasOrderTimeout;

    let res: any;
    if (MOCKING_CHECKOUT) {
      res = await submitUserInteraction(orderId, communicationId, {
        response: request,
      });
    } else if (isTimeoutConnect) {
      const { currentStamp, token } = (await getTOtp()) ?? {};

      const connectionInfo = encryptData(
        {
          retailerId: order.retailerId,
          credential: {
            username: (
              request as UserRetailerCheckoutRetailerCredentialsCommunicationResponse
            ).credential.username,
            password: (
              request as UserRetailerCheckoutRetailerCredentialsCommunicationResponse
            ).credential.password,
          },
        },
        token
      );
      res = await reSubmitCheckout({
        orderId,
        currentStamp,
        connectionInfo,
      });
    } else {
      res = await submitUserInteraction(orderId, communicationId, {
        response: request,
      });
    }

    return res;
  }
);

export const checkoutMachine = (() => {
  const machineId = "checkoutMachine";
  return createMachine(
    {
      id: machineId,
      initial: "polling",
      types: {} as unknown as {
        context: CheckoutMachineContext;
        events: CheckoutMachineEvents;
      },
      on: {
        ON_SESSION_OPEN: {
          actions: ["openSection"],
        },
        ON_SESSION_CLOSE: {
          actions: ["closeSection"],
        },
        UPDATE_RETAILER_CHECKOUT_ORDER: {
          actions: ["updateRetailerCheckoutOrder"],
        },
        ON_SWITCH_TO_GUEST: {
          target: "#checkoutMachine.switchToGuest",
        },
        ON_RETRY_CHECKOUT: {
          target: "#checkoutMachine.interactionTimeout.retryCheckout",
        },
      },

      context: ({
        input: { context },
      }: {
        input: { context: CheckoutMachineContext };
      }) => context,
      states: {
        waiting: {
          entry: [
            assign({
              checkoutState: CheckoutState.LOADING,
              activeStage: 1,
            }),
          ],
        },
        polling: {
          invoke: {
            src: "pollCheckoutStatus",
            input: ({ context, event }) => ({
              context,
              event,
            }),
            onDone: [
              {
                target: "#checkoutMachine.interactionTimeout",
                guard: "isOrderTimeout",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
              {
                target: "receiptGenerated",
                guard: "isCheckoutReceiptGeneratedStage",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
              {
                target: "processing.actionRequiredError",
                guard: "isActionRequiredErrorStage",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
              {
                target: "processing.nonActionRequiredError",
                guard: "isCheckoutNonActionRequiredErrorStage",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
              {
                target: "pollAgain",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
            ],
          },
        },
        pollAgain: {
          after: {
            [POLL_INTERVAL]: {
              target: "polling",
              guard: "shouldContinuePolling",
            },
          },
        },
        processing: {
          initial: "noError",
          entry: [
            assign({
              checkoutState: CheckoutState.PROCESSING,
            }),
          ],
          states: {
            noError: {},
            actionRequiredError: {
              entry: [
                assign({
                  checkoutState: CheckoutState.ACTION_REQUIRED_ERROR,
                }),
              ],
              initial: "polling",
              on: {
                SUBMIT_USER_INTERACTION: {
                  target: "interactionProcessing",
                },
              },
              // poll response to see if the user interaction is successful
              // if not, keep polling
              states: {
                polling: {
                  invoke: {
                    input: ({ context, event }) => ({
                      context,
                      event,
                    }),
                    src: "pollCheckoutStatus",
                    onDone: [
                      {
                        target: "pollAgain",
                        guard: "isUserInteractionRequired",
                      },
                      {
                        target: "#checkoutMachine.polling",
                      },
                    ],
                    onError: [
                      {
                        target: "#checkoutMachine.polling",
                      },
                    ],
                  },
                },
                pollAgain: {
                  after: {
                    [POLL_INTERVAL]: {
                      target: "polling",
                    },
                  },
                },
              },
            },
            interactionProcessing: {
              invoke: {
                input: ({ context, event }) => ({
                  context,
                  event,
                }),
                src: "submitUserInteractionService",
                onDone: [
                  {
                    target: "#checkoutMachine.polling",
                    actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
                  },
                ],
                onError: [
                  {
                    target: "#checkoutMachine.polling",
                  },
                ],
              },
            },
            nonActionRequiredError: {
              entry: assign({
                checkoutState: CheckoutState.NON_ACTION_REQUIRED_ERROR,
              }),
            },
          },
        },
        interactionTimeout: {
          entry: [],
          initial: "waitingForRetry",

          states: {
            waitingForRetry: {},
            retryCheckout: {
              invoke: {
                input: ({ context, event }) => ({
                  context,
                  event,
                }),
                src: "retryCheckout",
                onDone: [
                  {
                    target: "#checkoutMachine.polling",
                    actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
                  },
                ],
              },
            },
          },
        },
        switchToGuest: {
          invoke: {
            input: ({ context, event }) => ({
              context,
              event,
            }),
            src: "switchToGuest",
            onDone: [
              {
                target: "#checkoutMachine.polling",
                actions: ["updateRetailerCheckoutOrderStatusFromPoller"],
              },
            ],
          },
        },
        receiptGenerated: {
          entry: assign({
            checkoutState: CheckoutState.RECEIPT_GENERATED,
          }),
        },
      },
    },
    {
      actors: {
        submitUserInteractionService: submitUserInteractionApi,
        pollCheckoutStatus: pollCheckoutStatusActor,
        retryCheckout: retryCheckoutActor,
        switchToGuest: switchToGuestActor,
      },
      actions: {
        openSection: assign({
          sectionState: ({ event, context }) =>
            event?.type === "ON_SESSION_OPEN"
              ? {
                  ...context.sectionState,
                  [event.data.sessionToOpen]: {
                    ...context.sectionState[event.data.sessionToOpen],
                    open: true,
                  },
                }
              : context.sectionState,
        }),
        closeSection: assign({
          sectionState: ({ event, context }) =>
            event?.type === "ON_SESSION_CLOSE"
              ? {
                  ...context.sectionState,
                  [event.data.sessionToClose]: {
                    ...context.sectionState[event.data.sessionToClose],
                    open: false,
                  },
                }
              : context.sectionState,
        }),
        updateRetailerCheckoutOrder: assign({
          retailerCheckoutOrderStatus: ({ event, context }) => {
            const existingOrder =
              context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder;
            const newOrder = (event as CheckoutUpdateRetailerCheckoutOrderEvent)
              .data.retailerCheckoutOrder;
            return {
              ...context.retailerCheckoutOrderStatus,
              userRetailerCheckoutOrder: { ...existingOrder, ...newOrder },
            } as RetailerCheckoutOrderStatusV2;
          },
        }),
        updateRetailerCheckoutOrderStatusFromPoller: assign(
          ({
            event,
            context,
          }: {
            event: CheckoutMachineEvents;
            context: CheckoutMachineContext;
          }) => {
            const eventData =
              event as DoneActorEvent<RetailerCheckoutOrderStatusV2>;
            const products = eventData.output?.products;
            const statusData = eventData.output;
            const order = statusData?.userRetailerCheckoutOrder;

            const errorType = getErrorTypeFromCheckoutOrder(order);
            const stage = getActiveStageFromCheckoutSection(
              eventData.output.headerDisplayConfig?.stage
            );

            return {
              products,
              sectionState: getSectionStateFromCheckoutOrder(
                context.sectionState,
                order
              ),
              checkoutState: errorType
                ? CheckoutState.ACTION_REQUIRED_ERROR
                : CheckoutState.PROCESSING,
              retailerCheckoutOrderStatus: statusData,
              activeStage: getActiveStageFromCheckoutSection(
                eventData.output.headerDisplayConfig?.stage
              ),
              error: !errorType
                ? undefined
                : {
                    stage,
                    type: errorType,
                    interactionRequired: order?.userInteractionRequirement,
                    errorMessage:
                      order?.userInteractionRequirement?.displayMessage,
                  },
            };
          }
        ),

        stopPolling: () => {
          // This is a no-op action that just signals the polling should stop
        },
      },
      guards: {
        isUserInteractionRequired: ({ event }) => {
          if (!("output" in event)) return false;

          const { userRetailerCheckoutOrder } = (
            event as DoneActorEvent<{
              userRetailerCheckoutOrder: UserRetailerCheckoutOrder;
            }>
          )?.output;

          return !!userRetailerCheckoutOrder.userInteractionRequirement?.type;
        },
        isOrderTimeout: ({ event }) => {
          if (!("output" in event)) return false;

          const { userRetailerCheckoutOrder } = (
            event as DoneActorEvent<{
              userRetailerCheckoutOrder: UserRetailerCheckoutOrder;
            }>
          )?.output;

          return (
            userRetailerCheckoutOrder.status ===
            UserRetailerCheckoutOrderStatusV2.user_interaction_timeout
          );
        },
        isCheckoutNonActionRequiredErrorStage: () => {
          return false;
        },
        isCheckoutReceiptGeneratedStage: ({ event }) => {
          if (!("output" in event)) return false;
          const { userRetailerCheckoutOrder } = (
            event as DoneActorEvent<{
              userRetailerCheckoutOrder: UserRetailerCheckoutOrder;
            }>
          )?.output;
          return (
            userRetailerCheckoutOrder.status ===
              UserRetailerCheckoutOrderStatusV2.checkout_cancelled ||
            userRetailerCheckoutOrder.status ===
              UserRetailerCheckoutOrderStatusV2.checkout_error ||
            userRetailerCheckoutOrder.status ===
              UserRetailerCheckoutOrderStatusV2.checkout_success
          );
        },
        isActionRequiredErrorStage: ({ event }) => {
          if (!("output" in event)) return false;

          const { userRetailerCheckoutOrder } = (
            event as DoneActorEvent<{
              userRetailerCheckoutOrder: UserRetailerCheckoutOrder;
            }>
          )?.output;

          const type =
            userRetailerCheckoutOrder.userInteractionRequirement?.type;
          return !!type;
        },
        shouldContinuePolling: ({ context }) => {
          const order =
            context.retailerCheckoutOrderStatus?.userRetailerCheckoutOrder;

          const shouldContinue =
            context.checkoutState === CheckoutState.PROCESSING ||
            (context.checkoutState === CheckoutState.ACTION_REQUIRED_ERROR &&
              order?.userInteractionRequirement?.type ===
                UserInteractionType.OTP_VERIFICATION);

          return shouldContinue;
        },
      },
    }
  );
})();
